<template>
  <div :data-block-id="block.id">
    <p
      v-if="block.type === 'paragraph'"
      class="text-[17px] leading-8 text-[var(--arad-text)] sm:text-lg sm:leading-9"
    >
      <BlogInlineText :text="block.text" />
    </p>

    <component
      :is="block.level === 3 ? 'h3' : 'h2'"
      v-else-if="block.type === 'heading'"
      :id="block.id"
      class="scroll-mt-24 font-bold text-[var(--arad-text)]"
      :class="block.level === 3 ? 'text-lg sm:text-xl' : 'text-xl sm:text-2xl'"
    >
      <BlogInlineText :text="block.text" />
    </component>

    <component
      :is="block.kind === 'number' ? 'ol' : 'ul'"
      v-else-if="block.type === 'list'"
      class="ms-5 space-y-1.5 text-[17px] leading-8"
      :class="block.kind === 'number' ? 'list-decimal' : 'list-disc'"
    >
      <li
        v-for="(item, index) in block.items"
        :key="index"
      >
        <BlogInlineText :text="item" />
      </li>
    </component>

    <figure
      v-else-if="block.type === 'image' && imageSrc"
      class="space-y-2"
    >
      <img
        :src="imageSrc"
        :alt="block.alt"
        class="w-full rounded-2xl object-cover"
      >
      <figcaption
        v-if="block.caption"
        class="text-center text-sm text-[var(--ui-text-muted)]"
      >
        {{ block.caption }}
      </figcaption>
    </figure>

    <div
      v-else-if="block.type === 'gallery'"
      class="grid gap-3"
      :class="galleryClass"
    >
      <img
        v-for="(item, index) in galleryItems"
        :key="index"
        :src="item.src"
        :alt="item.alt"
        class="aspect-video w-full rounded-2xl object-cover"
      >
    </div>

    <BlogBeforeAfter
      v-else-if="block.type === 'beforeAfter'"
      :before-src="block.beforeSrc || (block.beforeMediaId ? media?.[block.beforeMediaId] : '') || ''"
      :after-src="block.afterSrc || (block.afterMediaId ? media?.[block.afterMediaId] : '') || ''"
    />

    <blockquote
      v-else-if="block.type === 'quote'"
      class="border-s-4 border-primary/50 bg-[var(--ui-bg-muted)] px-4 py-3 text-[17px] leading-8"
    >
      <BlogInlineText :text="block.text" />
      <footer
        v-if="block.cite"
        class="mt-2 text-sm text-[var(--ui-text-muted)]"
      >
        — {{ block.cite }}
      </footer>
    </blockquote>

    <div
      v-else-if="block.type === 'table'"
      class="overflow-x-auto rounded-2xl border border-[var(--ui-border)]"
    >
      <table class="w-full min-w-[28rem] text-sm">
        <thead class="bg-[var(--ui-bg-muted)]">
          <tr>
            <th
              v-for="(header, index) in block.headers"
              :key="index"
              class="px-3 py-2 text-right font-semibold"
            >
              {{ header }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, rIndex) in block.rows"
            :key="rIndex"
            class="border-t border-[var(--ui-border)]"
          >
            <td
              v-for="(cell, cIndex) in row"
              :key="cIndex"
              class="px-3 py-2"
            >
              {{ cell }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-else-if="block.type === 'video' && embedSrc"
      class="aspect-video overflow-hidden rounded-2xl bg-black"
    >
      <iframe
        :src="embedSrc"
        class="size-full border-0"
        allowfullscreen
        title="ویدیو"
      />
    </div>

    <figure
      v-else-if="block.type === 'audio' && safeAudioUrl"
      class="space-y-2 rounded-2xl border border-[var(--ui-border)] bg-[var(--ui-bg-muted)]/40 p-4"
    >
      <figcaption
        v-if="block.caption"
        class="text-sm font-medium"
      >
        {{ block.caption }}
      </figcaption>
      <audio
        :src="safeAudioUrl"
        controls
        preload="metadata"
        class="w-full"
      />
    </figure>

    <hr
      v-else-if="block.type === 'divider'"
      class="border-[var(--ui-border)]"
    >

    <a
      v-else-if="block.type === 'link' && safeLinkHref"
      :href="safeLinkHref"
      class="group inline-flex items-center gap-2 transition"
      :class="linkClass"
      :target="safeLinkHref.startsWith('http') ? '_blank' : undefined"
      :rel="safeLinkHref.startsWith('http') ? 'noopener noreferrer' : undefined"
    >
      <UIcon
        name="i-lucide-external-link"
        class="size-4 shrink-0"
        :class="block.style === 'inline' ? 'hidden' : ''"
      />
      {{ block.label || safeLinkHref }}
    </a>

    <div
      v-else-if="(block.type === 'quiz' && (preview || block.quizId)) || (block.type === 'survey' && (preview || block.surveyId))"
      class="overflow-hidden rounded-2xl border border-[var(--ui-border)]"
    >
      <div class="flex items-center gap-3 bg-[var(--ui-bg-muted)]/50 p-4">
        <span class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <UIcon
            :name="block.type === 'quiz' ? 'i-lucide-list-checks' : 'i-lucide-clipboard-list'"
            class="size-5"
          />
        </span>
        <div class="min-w-0">
          <p class="text-xs text-[var(--ui-text-muted)]">
            {{ block.type === 'quiz' ? 'آزمون' : 'نظرسنجی' }}
          </p>
          <p class="font-semibold">
            {{ block.type === 'quiz' ? (block.quizTitle || 'آزمون انتخاب‌شده') : (block.surveyTitle || 'نظرسنجی انتخاب‌شده') }}
          </p>
        </div>
      </div>
      <div
        v-if="preview"
        class="p-4 text-sm text-[var(--ui-text-muted)]"
      >
        {{ block.type === 'quiz' ? 'آزمون در صفحهٔ منتشرشده قابل انجام است.' : 'نظرسنجی در صفحهٔ منتشرشده قابل تکمیل است.' }}
      </div>
      <BlockLeadQuiz
        v-else-if="block.type === 'quiz'"
        :quiz-id="block.quizId"
        :title="block.quizTitle"
        compact
      />
      <BlockLeadSurvey
        v-else
        :survey-id="block.surveyId"
        :title="block.surveyTitle"
        compact
      />
    </div>

    <aside
      v-else-if="block.type === 'cta'"
      class="overflow-hidden rounded-2xl border border-[var(--ui-border)] bg-[var(--arad-surface)]"
    >
      <div class="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:gap-5 sm:p-5">
        <span
          class="inline-flex size-11 shrink-0 items-center justify-center rounded-xl"
          :class="block.action === 'booking'
            ? 'bg-primary/10 text-primary'
            : 'bg-[var(--ui-bg-muted)] text-[var(--ui-text-muted)]'"
        >
          <UIcon
            :name="block.action === 'booking' ? 'i-lucide-calendar-plus' : 'i-lucide-external-link'"
            class="size-5"
          />
        </span>
        <div class="min-w-0 flex-1 space-y-1.5">
          <p
            v-if="block.action === 'booking' && (block.serviceTitle || block.serviceRef)"
            class="text-[11px] font-medium text-primary"
          >
            {{ block.serviceTitle || 'خدمت انتخاب‌شده' }}
          </p>
          <p class="text-base font-semibold leading-6 sm:text-lg">
            {{ block.title }}
          </p>
          <p
            v-if="block.description"
            class="text-sm leading-6 text-[var(--ui-text-muted)]"
          >
            {{ block.description }}
          </p>
        </div>
        <UButton
          class="w-full shrink-0 sm:w-auto"
          size="md"
          :trailing-icon="block.action === 'booking' ? 'i-lucide-arrow-left' : 'i-lucide-arrow-up-left'"
          :to="preview || block.action !== 'link' ? undefined : block.href"
          :target="block.action === 'link' && block.href ? '_blank' : undefined"
          @click="onCta"
        >
          {{ block.buttonLabel }}
        </UButton>
      </div>
    </aside>

    <section
      v-else-if="block.type === 'keyPoints'"
      class="rounded-3xl border border-primary/20 bg-primary/5 p-5"
    >
      <p class="mb-3 text-sm font-semibold text-primary">
        {{ block.title || 'نکات کلیدی' }}
      </p>
      <ul class="space-y-2">
        <li
          v-for="(item, index) in block.items"
          :key="index"
          class="flex gap-2 text-[15px] leading-7"
        >
          <UIcon
            name="i-lucide-check"
            class="mt-1 size-4 shrink-0 text-primary"
          />
          <BlogInlineText :text="item" />
        </li>
      </ul>
    </section>

    <section
      v-else-if="block.type === 'medicalAlert'"
      class="rounded-3xl border p-5"
      :class="alertClass"
    >
      <p class="mb-1 text-sm font-semibold">
        {{ block.title || 'توجه' }}
      </p>
      <p class="text-[15px] leading-7">
        <BlogInlineText :text="block.text" />
      </p>
    </section>

    <section
      v-else-if="block.type === 'faq' && (!block.layout || block.layout === 'accordion')"
      class="space-y-3"
    >
      <p
        v-if="block.title"
        class="font-semibold"
      >
        {{ block.title }}
      </p>
      <div
        v-for="(item, index) in block.items"
        :key="index"
        class="rounded-2xl border border-[var(--ui-border)]"
      >
        <details class="group p-4">
          <summary class="flex cursor-pointer list-none items-center justify-between gap-3 font-medium">
            {{ item.question }}
            <UIcon
              name="i-lucide-chevron-down"
              class="size-4 shrink-0 transition group-open:rotate-180"
            />
          </summary>
          <p class="mt-3 text-sm leading-7 text-[var(--ui-text-muted)]">
            <BlogInlineText :text="item.answer" />
          </p>
        </details>
      </div>
    </section>

    <section
      v-else-if="block.type === 'faq' && block.layout === 'cards'"
      class="space-y-3"
    >
      <p
        v-if="block.title"
        class="font-semibold"
      >
        {{ block.title }}
      </p>
      <div
        v-for="(item, index) in block.items"
        :key="index"
        class="rounded-2xl border border-[var(--ui-border)] bg-[var(--ui-bg-muted)]/35 p-4"
      >
        <p class="font-medium">{{ item.question }}</p>
        <p class="mt-2 text-sm leading-7 text-[var(--ui-text-muted)]">
          <BlogInlineText :text="item.answer" />
        </p>
      </div>
    </section>

    <section
      v-else-if="block.type === 'faq'"
      class="space-y-3"
    >
      <p
        v-if="block.title"
        class="font-semibold"
      >
        {{ block.title }}
      </p>
      <dl class="divide-y divide-[var(--ui-border)]">
        <div
          v-for="(item, index) in block.items"
          :key="index"
          class="py-3 first:pt-0 last:pb-0"
        >
          <dt class="font-medium">{{ item.question }}</dt>
          <dd class="mt-1 text-sm leading-7 text-[var(--ui-text-muted)]">
            <BlogInlineText :text="item.answer" />
          </dd>
        </div>
      </dl>
    </section>

    <ol
      v-else-if="block.type === 'steps'"
      class="space-y-3"
    >
      <li
        v-for="(item, index) in block.items"
        :key="index"
        class="flex gap-3"
      >
        <span class="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">
          {{ index + 1 }}
        </span>
        <div>
          <p class="font-semibold">
            {{ item.title }}
          </p>
          <p
            v-if="item.text"
            class="mt-0.5 text-sm leading-7 text-[var(--ui-text-muted)]"
          >
            <BlogInlineText :text="item.text" />
          </p>
        </div>
      </li>
    </ol>

    <section
      v-else-if="block.type === 'symptomChecklist'"
      class="rounded-3xl border border-[var(--ui-border)] p-5"
    >
      <p class="mb-3 font-semibold">
        {{ block.title || 'علائم' }}
      </p>
      <ul class="grid gap-2 sm:grid-cols-2">
        <li
          v-for="(item, index) in block.items"
          :key="index"
          class="flex gap-2 text-sm"
        >
          <UIcon
            name="i-lucide-circle-dot"
            class="mt-0.5 size-4 text-primary"
          />
          {{ item }}
        </li>
      </ul>
    </section>

    <section
      v-else-if="block.type === 'doNotList'"
      class="rounded-3xl border border-red-200 bg-red-50/70 p-5 dark:border-red-500/20 dark:bg-red-950/30"
    >
      <p class="mb-3 font-semibold text-red-800 dark:text-red-100">
        {{ block.title || 'چه کارهایی نکنیم' }}
      </p>
      <ul class="space-y-2">
        <li
          v-for="(item, index) in block.items"
          :key="index"
          class="flex gap-2 text-sm"
        >
          <UIcon
            name="i-lucide-x"
            class="mt-0.5 size-4 text-red-600"
          />
          {{ item }}
        </li>
      </ul>
    </section>

    <section
      v-else-if="block.type === 'references'"
      class="space-y-2 text-sm"
    >
      <p class="font-semibold">
        {{ block.title || 'منابع' }}
      </p>
      <ol class="list-decimal space-y-1.5 ps-5">
        <li
          v-for="(item, index) in block.items"
          :key="index"
        >
          <span>{{ item.authors ? `${item.authors}. ` : '' }}{{ item.title }}{{ item.year ? ` (${item.year})` : '' }}</span>
          <a
            v-if="item.url"
            :href="item.url"
            class="ms-1 text-primary"
            target="_blank"
            rel="noopener noreferrer"
          >لینک</a>
        </li>
      </ol>
    </section>

    <section
      v-else-if="block.type === 'doctorNote'"
      class="rounded-3xl bg-[var(--ui-bg-muted)] p-5"
    >
      <p class="mb-1 text-sm font-semibold text-primary">
        {{ block.title || 'توصیهٔ پزشک' }}
      </p>
      <p class="text-[15px] leading-7">
        <BlogInlineText :text="block.text" />
      </p>
    </section>

    <p
      v-else-if="block.type === 'disclaimer'"
      class="rounded-2xl bg-[var(--ui-bg-muted)] px-4 py-3 text-xs leading-6 text-[var(--ui-text-muted)]"
    >
      {{ block.text }}
    </p>

    <BlogArticleBuilderBlock
      v-else-if="block.type === 'builder'"
      :block="block"
      :preview="preview"
    />

    <BlogArticleServiceDoctorsBlock
      v-else-if="block.type === 'serviceDoctors'"
      :block="block"
      :service-slug="serviceSlug"
      :preview="preview"
    />
  </div>
</template>

<script setup lang="ts">
import type { BlogBlock, BlogPreviewDevice } from '~/types/blog'
import BlogBeforeAfter from '~/components/blog/BeforeAfter.vue'
import BlockLeadQuiz from '~/components/blocks/BlockLeadQuiz.vue'
import BlockLeadSurvey from '~/components/blocks/BlockLeadSurvey.vue'
import { useBookingRequest } from '~/composables/useBookingRequest'
import { isSafeHref } from '~/utils/blog/inline'

const props = defineProps<{
  block: BlogBlock
  media?: Record<string, string>
  device?: BlogPreviewDevice
  preview?: boolean
  serviceSlug?: string
}>()

const { requestBooking } = useBookingRequest()

const safeLinkHref = computed(() => {
  if (props.block.type !== 'link') return ''
  const href = props.block.href.trim()
  return isSafeHref(href) ? href : ''
})

const safeAudioUrl = computed(() => {
  if (props.block.type !== 'audio') return ''
  const url = props.block.url.trim()
  return isSafeHref(url) ? url : ''
})

const linkClass = computed(() => {
  if (props.block.type !== 'link') return ''
  if (props.block.style === 'card') return 'w-full rounded-2xl border border-[var(--ui-border)] bg-[var(--ui-bg-muted)]/50 p-4 font-semibold text-[var(--arad-text)] hover:border-primary/40 hover:bg-primary/5'
  if (props.block.style === 'button') return 'rounded-xl bg-primary px-4 py-2.5 font-semibold text-white shadow-sm hover:bg-primary/90'
  return 'font-medium text-primary underline underline-offset-4 hover:text-primary/80'
})

const imageSrc = computed(() => {
  if (props.block.type !== 'image') return ''
  if (props.block.src) return props.block.src
  if (props.block.mediaId) return props.media?.[props.block.mediaId] ?? ''
  return ''
})

const galleryItems = computed(() => {
  if (props.block.type !== 'gallery') return []
  return props.block.items
    .map((item) => ({
      src: item.src || (item.mediaId ? props.media?.[item.mediaId] : '') || '',
      alt: item.alt,
    }))
    .filter((item) => item.src)
})

const embedSrc = computed(() => {
  if (props.block.type !== 'video') return ''
  const url = props.block.url.trim()
  const yt = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{6,})/.exec(url)
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`
  const aparat = /aparat\.com\/(?:v\/|video\/video\/embed\/videohash\/)([\w-]+)/.exec(url)
  if (aparat) return `https://www.aparat.com/video/video/embed/videohash/${aparat[1]}/vt/frame`
  if (url.startsWith('https://')) return url
  return ''
})

const alertClass = computed(() => {
  if (props.block.type !== 'medicalAlert') return ''
  if (props.block.tone === 'danger') return 'border-red-200 bg-red-50 text-red-950 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-50'
  if (props.block.tone === 'info') return 'border-sky-200 bg-sky-50 text-sky-950 dark:border-sky-500/30 dark:bg-sky-950/40 dark:text-sky-50'
  return 'border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-50'
})

const galleryClass = computed(() => {
  if (props.device === 'mobile') return 'grid-cols-1'
  if (props.device === 'tablet' || props.device === 'desktop') return 'grid-cols-2'
  return 'sm:grid-cols-2'
})

function onCta() {
  if (props.preview) return
  if (props.block.type !== 'cta' || props.block.action !== 'booking') return
  requestBooking(props.block.serviceRef || 'blog')
}
</script>
